import { defineStore } from 'pinia';
import { reactive } from 'vue';
import { ISearchForecastForReceiptState } from '../domains/entities/searchForecastForReceipt';
import { ForecastForReceiptService } from '../domains/services/forecastForReceiptService';
import { diContainer } from '@/coreContainer/container';
import { ForecastForReceiptRepository } from '@/coreInfrastructureModule/Repositories/ForecastForReceiptRepository';
import { INetworkResult } from '@/coreDomainsModule/networkResult';
import { IError } from '@/coreInfrastructureModule/interfaces/IError';
import { IYearMonth } from '@/coreDomainsModule/date';
import { DownloadForecastForReceiptExcelUseCase } from '../useCases/downloadForecastForReceiptExcelUseCase';
import { saveAs } from 'file-saver';

export const useSearchForecastForReceiptStore = defineStore('useSearchForecastForReceipt', () => {
  const now = new Date();
  // 前月を計算（仕様: 初期値は操作日の前月）
  const previousMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const state = reactive<ISearchForecastForReceiptState>({
    receivedForecastYearMonth: {
      year: previousMonth.getFullYear(),
      month: previousMonth.getMonth() + 1, // IYearMonth uses 1-indexed month
    },
    staffFilterTableContents: {
      renderingKey: 0,
      contentItems: [],
      checkedIds: [],
      loadingState: {
        status: {
          type: 'success',
        },
      },
    },
    keywordFilterTableContents: {
      renderingKey: 0,
      contentItems: [],
      checkedIds: [],
      loadingState: {
        status: {
          type: 'success',
        },
      },
    },
    searchResultTableContents: {
      renderingKey: 0,
      contentItems: [],
      checkedIds: [],
      loadingState: {
        status: {
          type: 'success',
        },
      },
    },
  });

  /**
   * stateの初期化
   */
  const $reset = () => {
    const now = new Date();
    // 前月を計算（仕様: 初期値は操作日の前月）
    const previousMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    state.receivedForecastYearMonth = {
      year: previousMonth.getFullYear(),
      month: previousMonth.getMonth() + 1,
    };
    state.staffFilterTableContents = {
      renderingKey: 0,
      contentItems: [],
      checkedIds: [],
      loadingState: {
        status: {
          type: 'success',
        },
      },
    };
    state.keywordFilterTableContents = {
      renderingKey: 0,
      contentItems: [],
      checkedIds: [],
      loadingState: {
        status: {
          type: 'success',
        },
      },
    };
    state.searchResultTableContents = {
      renderingKey: 0,
      contentItems: [],
      checkedIds: [],
      loadingState: {
        status: {
          type: 'success',
        },
      },
    };
  };

  const bulkGetForecastForReceiptStaffUseCase = diContainer.bulkGetForecastForReceiptStaffUseCase;
  const getForecastForReceiptSummaryUseCase = diContainer.getForecastForReceiptSummaryUseCase;

  /**
   * 表示内容を取得する
   */
  const fetchStaffFilterTableContents = async () => {
    state.staffFilterTableContents.loadingState = {
      status: {
        type: 'loading',
      },
    };

    const staffResult = await bulkGetForecastForReceiptStaffUseCase.Execute(
      state.receivedForecastYearMonth.year,
      state.receivedForecastYearMonth.month
    );

    switch (staffResult.status.type) {
      case 'success':
        state.staffFilterTableContents.contentItems = ForecastForReceiptService.displayContentStaffFilterTable(staffResult.status.data);
        state.staffFilterTableContents.loadingState = {
          status: {
            type: 'success',
          },
        };
        break;
      case 'error':
        state.staffFilterTableContents.loadingState = {
          status: {
            type: 'error',
            errorMessage: 'データ取得に失敗しました',
          },
        };
        break;
    }
  };

  /**
   * 検索結果テーブルの表示内容を取得する
   * @param userIds - フィルタする担当者IDリスト（任意）
   */
  const fetchSearchResultTableContents = async (userIds?: string[]) => {
    state.searchResultTableContents.loadingState = {
      status: {
        type: 'loading',
      },
    };

    const summaryResult = await getForecastForReceiptSummaryUseCase.Execute(
      state.receivedForecastYearMonth.year,
      state.receivedForecastYearMonth.month,
      userIds
    );

    switch (summaryResult.status.type) {
      case 'success':
        state.searchResultTableContents.contentItems = ForecastForReceiptService.displayContentSearchResultTable(summaryResult.status.data);
        state.searchResultTableContents.loadingState = {
          status: {
            type: 'success',
          },
        };
        break;
      case 'error':
        state.searchResultTableContents.loadingState = {
          status: {
            type: 'error',
            errorMessage: 'データ取得に失敗しました',
          },
        };
        break;
    }
  };

  /**
   * 内示受領月が変更された時の処理
   * @param yearMonth - 選択された年月
   */
  const onSelectedReceiveMonth = async (yearMonth: { year: number; month: number }) => {
    state.receivedForecastYearMonth = yearMonth;
    await Promise.all([fetchSearchResultTableContents(), fetchStaffFilterTableContents()]);
  };

  /**
   * 検索結果テーブルのチェック状態を更新する
   * @param checkedDisplayIds - チェックされた表示ID一覧
   */
  const onCheckedSearchResultDisplayIds = (checkedDisplayIds: string[]) => {
    state.searchResultTableContents.checkedIds = checkedDisplayIds;
  };

  /**
   * 担当者絞り込みテーブルのチェック状態を更新し、自動検索を実行する
   * @param checkedDisplayIds - チェックされた表示ID一覧
   */
  const onCheckedStaffFilterDisplayIds = async (checkedDisplayIds: string[]) => {
    state.staffFilterTableContents.checkedIds = checkedDisplayIds;

    // displayIdからforecastStaffId（userId）を取得
    const selectedUserIds = state.staffFilterTableContents.contentItems
      .filter((item) => checkedDisplayIds.includes(item.displayId))
      .map((item) => item.forecastStaffId as string)
      .filter((id): id is string => id !== undefined && id !== '');

    // 自動検索を実行（仕様: 絞り込み条件のUI変更イベントで自動的に検索が行われる）
    await fetchSearchResultTableContents(selectedUserIds.length > 0 ? selectedUserIds : undefined);
  };

  /**
   * 受領用内示の担当者を一括更新する
   * @param forecastForReceiptIds - 更新対象の受領用内示IDリスト
   * @param userIds - 担当者IDリスト
   * @param forecastForReceiptName - 受領用内示名（名称変更時に使用）
   */
  const bulkUpdateForecastForReceipt = async (
    forecastForReceiptIds: string[],
    userIds: string[],
    forecastForReceiptName?: string
  ): Promise<INetworkResult<void, IError>> => {
    const repository = new ForecastForReceiptRepository();
    return await repository.bulkUpdateForecastForReceipt({
      bulkUpdateForecastForReceiptInfo: {
        forecastForReceiptIds,
        forecastForReceiptResponsibleUserIdList: userIds,
        forecastForReceiptName,
      },
    });
  };

  /**
   * 受領用内示の客先内示データをエクスポートしてダウンロードURLを取得する
   * @param userId - ダウンロード対象の受領用内示のユーザーID
   * @param startYearMonth - 開始年月
   * @param forecastForReceiptName - 受領用内示名
   * @returns ダウンロードURL情報またはエラー
   */
  const downloadForecastForReceipt = async (
    userId: string,
    startYearMonth: IYearMonth,
    forecastForReceiptName?: string
  ): Promise<boolean> => {
    const result = await diContainer.getOutputForecastForReceiptExcelDataUseCase.Execute(userId);

    switch (result.status.type) {
      case 'success':
        let data = result.status.data;
        if (forecastForReceiptName) {
          for (const [key, _] of Object.entries(result.status.data.excelInputInfo)) {
            if (key === forecastForReceiptName) {
              data = {
                templateForecastForReceiptExcelData: result.status.data.templateForecastForReceiptExcelData,
                excelInputInfo: { [key]: result.status.data.excelInputInfo[key] },
              };
              break;
            }
          }
        }
        const downloadExcelUseCase = new DownloadForecastForReceiptExcelUseCase();
        const downloadResult = await downloadExcelUseCase.Execute(data, startYearMonth);
        saveAs(downloadResult, '受領用内示.zip');
        return true;
      case 'error':
      default:
        return false;
    }
  };

  return {
    state,
    $reset,
    fetchStaffFilterTableContents,
    fetchSearchResultTableContents,
    onSelectedReceiveMonth,
    onCheckedSearchResultDisplayIds,
    onCheckedStaffFilterDisplayIds,
    bulkUpdateForecastForReceipt,
    downloadForecastForReceipt,
  };
});
